import { onMounted, onUpdated, computed } from "vue"
import { ref } from "@vue/reactivity"
import { KNOLIA } from "@/knolia"
import { Gems } from "@/gems"
import { MESSAGES } from "../messages"
import { useUserStore } from "@/store/modules/user"
import router from "../router"

export function useValidateSkills() {
    const isLowerThanMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 300 : false)
    const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 600  && window.innerWidth > 300 : false)
    const validateSkills = ref(false)
    const validationList = ref(false)
    const ratingType = ref(2)
    const userFilter = ref('')
    const showOnlySelf = ref(false)
    const requestDateAsc = ref(true)
    const showUserRequests = ref(false)
    const userSelected = ref(null)
    const toggleAllTeams = ref(false)
    const validationListReference = ref(null)
    const userSelectedReference = ref(null)
    const userStore = useUserStore()
    const validationWithCertification = ref(false)
    const validationToTransfer = ref(false)
    const fetchingValidationList = ref(false)

    async function GetValidationList(shouldShowUserRequests = false) {
        fetchingValidationList.value = true
        await KNOLIA.CallBackEnd("post", "get-validation-list", { 
            data: { 
                ratingType: ratingType.value, 
                requestDateAsc: requestDateAsc.value, 
                validationWithCertification: validationWithCertification.value, 
                validationToTransfer: validationToTransfer.value,
                showOnlySelf: showOnlySelf.value
            } 
        })
        .then((res) => {
            validationList.value = res.data.teams
            showUserRequests.value = shouldShowUserRequests
            ToggleRequestDateSort()
            fetchingValidationList.value = false

            userStore.SetCountMessages(res.data.count_messages)
            userStore.SetCountValidations(res.data.request_count)
        });
    }

    async function Validate(elem) {
        if(elem.validation != 1)
            Gems.Telegram(MESSAGES.QUESTIONS.POST_VALIDATION, "tele-info", "tele-ok", "tele-cancel")
                .then(
                    () => {
                        PublishDecision(elem)
                    },
                    () => {}
                )
        else
            PublishDecision(elem)
    }

    function canChangeValidator(elem) {
        if(elem.validators_to_transfer.length > 0){
            return elem.history_id && elem.newValidatorId
        } else {
            return elem.newValidatorId
        }
    }

    async function ChangeValidator(elem) {
        if(!elem.newValidatorId) {
            Gems.Telegram(MESSAGES.ERROR.VALIDATOR_NOT_SELECTED, "tele-error")
            return
        }

        Gems.Telegram(MESSAGES.QUESTIONS.CHANGE_VALIDATOR, "tele-info", "tele-ok", "tele-cancel")
            .then(
                async () => {
                    elem.without_validator = false
                    elem.validator_id = elem.newValidatorId

                    KNOLIA.CallBackEnd("post", "control-rating-skills", { elem, type: 'change-validator' })
                        .then(async (res) => {
                            if(res.error) {
                                Gems.Telegram(MESSAGES.ERROR.DEFAULT, 'tele-not-allowed')
                                return
                            }

                            validationListReference.value = validationList.value
                            userSelectedReference.value = userSelected.value

                            await GetValidationList(showUserRequests.value)

                            validationListReference.value?.forEach(oldTeam => {
                                const newTeam = validationList.value.find(t => t.id === oldTeam.id) 
                                if (newTeam) {
                                    newTeam.showDetail = oldTeam.showDetail
                                    if(oldTeam.id == userSelectedReference.value.team_id)  {
                                        userSelected.value = newTeam.users.find(u => u.user_id == userSelectedReference.value.user_id)
                                    }
                                }
                            })
                        });
                },
                () => {}
            )
    }

    async function PublishDecision(elem) {
        KNOLIA.CallBackEnd("post", "control-rating-skills", { elem, type: 'validate' })
            .then(async (res) => {
                if(res.error) {
                    Gems.Telegram(MESSAGES.ERROR.DEFAULT, 'tele-not-allowed')
                    return
                }

                validationListReference.value = validationList.value
                userSelectedReference.value = userSelected.value

                await GetValidationList(showUserRequests.value)

                validationListReference.value?.forEach(oldTeam => {
                    const newTeam = validationList.value.find(t => t.id === oldTeam.id) 
                    if (newTeam) {
                        newTeam.showDetail = oldTeam.showDetail
                        if(oldTeam.id == userSelectedReference.value.team_id)  {
                            userSelected.value = newTeam.users.find(u => u.user_id == userSelectedReference.value.user_id)
                        }
                    }
                })
            });
    }

    function parseDateSafe(d) {
                if (!d) return null
                const t = new Date(d)
                return isNaN(t) ? null : t.getTime()
      }
    
    function sortUserRequestsArray(arr, asc) {
        if (!Array.isArray(arr)) return
        arr.sort((a, b) => {
            const da = parseDateSafe(a.request_date)
            const db = parseDateSafe(b.request_date)
            if (!da && !db) return 0
            if (!da) return 1
            if (!db) return -1
            return asc ? da - db : db - da
        })
    }

    function getLatestRequestTimestamp(requests) {
        if (!Array.isArray(requests) || requests.length === 0) return null
        let latest = null
        for (const r of requests) {
            const t = parseDateSafe(r.request_date)
            if (t === null) continue
            if (latest === null || t > latest) latest = t
        }
        return latest
    }

    function getTeamLatestTimestamp(team) {
        if (!team || !Array.isArray(team.users)) return null
        let latest = null
        for (const u of team.users) {
            const t = getLatestRequestTimestamp(u.requests)
            if (!t) continue
            if (latest === null || t > latest) latest = t
        }
        return latest
    }

    async function RequestValidationList() {
        const openStates = validationList.value.map(team => ({ id: team.id, showDetail: team.showDetail }))
        const prevSelected = userSelected.value ? { ...userSelected.value } : null
        const selectedUserId = userSelected.value?.user_id ?? null

        await GetValidationList()

        validationList.value.forEach(team => {
            const found = openStates.find(s => s.id === team.id)
            if (found) 
                team.showDetail = found.showDetail

            if(toggleAllTeams.value)
                team.showDetail = true
        })

        if (selectedUserId) {
            let foundUser = null
            for (const team of validationList.value) {
                if (!team.users) 
                    continue

                const u = team.users.find(x => x.user_id === selectedUserId)
                if (u) {
                    foundUser = u
                    userSelected.value = u
                    showUserRequests.value = true
                    break
                }
            }

            // If user not present in refreshed list, keep previous selection so sitemap remains visible
            if (!foundUser && prevSelected) {
                prevSelected.requests = []
                userSelected.value = prevSelected
                showUserRequests.value = true
            }
        }

        ToggleRequestDateSort()
    }

    function ToggleRequestDateSort(ascArg) {
        const asc = ascArg !== undefined ? ascArg : requestDateAsc.value
        if (showUserRequests.value && userSelected.value?.requests) {
            sortUserRequestsArray(userSelected.value.requests, asc)
        } else {
            validationList.value.forEach(team => {
                team.users?.forEach(u => sortUserRequestsArray(u.requests, asc))
                if (Array.isArray(team.users)) {
                    team.users.sort((a, b) => {
                        const da = getLatestRequestTimestamp(a.requests)
                        const db = getLatestRequestTimestamp(b.requests)
                        if (!da && !db) return 0
                        if (!da) return 1
                        if (!db) return -1
                        return asc ? da - db : db - da
                    })
                }
            })

            validationList.value.sort((a, b) => {
                const da = getTeamLatestTimestamp(a)
                const db = getTeamLatestTimestamp(b)
                if (!da && !db) return 0
                if (!da) return 1
                if (!db) return -1
                return asc ? da - db : db - da
            })
        }
    }

    function ShowUserRequests(user, team) {
        showUserRequests.value = !showUserRequests.value
        userFilter.value = ''
        if (team && team.designation) user.team_designation = team.designation
        userSelected.value = user
    }

    function ToggleAllTeams() {
        toggleAllTeams.value = !toggleAllTeams.value
        validationList.value.forEach(team => {
            team.showDetail = toggleAllTeams.value
        })
    }

    function BackToTeams() {
        showUserRequests.value = false
        userSelected.value = null
        userFilter.value = ''
    }

    function TeamDetail(team) {
        team.showDetail = !team.showDetail
        if(toggleAllTeams.value)
            toggleAllTeams.value = false
    }

    function SetCommentsRead(skill_rating_id, only_validators = false) { 
        validationList.value.forEach(team => {
            team.users.forEach(user => {
                user.requests.forEach(request => {
                    if(request.skills_ratings_id === skill_rating_id) {
                        let countToSubtract = only_validators ? request.has_unread_messages_only_validators : request.has_unread_messages

                        if(only_validators)
                            request.has_unread_messages_only_validators = 0
                        else
                            request.has_unread_messages = 0

                        user[`new_messages_count_${request.rating_type}`] = user[`new_messages_count_${request.rating_type}`] - countToSubtract
                        team[`new_messages_count_${request.rating_type}`] = team[`new_messages_count_${request.rating_type}`] - countToSubtract
                        user[`new_messages_count_3`] = user[`new_messages_count_1`] + user[`new_messages_count_2`]
                        team[`new_messages_count_3`] = team[`new_messages_count_1`] + team[`new_messages_count_2`]
                    }
                })
            }) 
        });
    }

    function SetDocumentsRead(skill_rating_id) { 
        validationList.value.forEach(team => {
            team.users.forEach(user => {
                user.requests.forEach(request => {
                    if(request.skills_ratings_id === skill_rating_id) 
                        request.has_files_not_viewed = 0
                })
            }) 
        });
    }


    function ToggleValidationWithCertification() { 
        validationWithCertification.value = true
        validationToTransfer.value = false
        RequestValidationList()
    }

    function ToggleValidationWithoutCertification() { 
        validationWithCertification.value = false
        validationToTransfer.value = false
        RequestValidationList()
    }

    function ToggleValidationToTransfer() {
        validationToTransfer.value = true
        RequestValidationList()
    }

    onMounted(() => {
        validateSkills.value = router.currentRoute.value.params.validateSkills
        if(validateSkills.value)
            GetValidationList()

        window.addEventListener('resize', () => {
            isMobile.value = KNOLIA.updateIsMobile()
            isLowerThanMobile.value = KNOLIA.updateIsLowerThanMobile()
        })
    })

    onUpdated(() => {
        validateSkills.value = router.currentRoute.value.params.validateSkills
        if(!validationList.value.length > 0 && validateSkills.value && !fetchingValidationList.value)
            GetValidationList()

        if(validationList.value)
            validationList.value.forEach((elem, i) => {
                const docIcon = document.getElementById(`doc-btn-${i}`)
                const chatIcon = document.getElementById(`chat-btn-${i}`)
                const validationChoose = document.getElementById(`validation-choose-${i}`)

                if(docIcon)
                    Gems.Tooltip(docIcon, 'Documentos', 'tooltip')
                
                if(chatIcon)
                    Gems.Tooltip(chatIcon, 'Mensagens', 'tooltip')
                
                if(validationChoose)
                    Gems.Tooltip(validationChoose, 'Confiança do validador', 'tooltip')
            })
    });

    const filteredValidationList = computed({
        get () {
            const filterText = KNOLIA.NormalizeString(userFilter?.value)
            const list = validationList?.value || []
            const out = []
            const ratingTypeInt = parseInt(ratingType.value)

            for (const team of list) {
                if (!(team[`rating_${ratingTypeInt}_count`] > 0) && ratingTypeInt !== 3)
                    continue

                const teamValue = KNOLIA.NormalizeString(team.designation)
                if (teamValue.includes(filterText)) {
                    out.push(team)
                    continue
                }

                if (!Array.isArray(team.users)) 
                    continue

                let anyUserMatched = false
                team.users.forEach(u => {
                    const isRatingVisible = u[`rating_${ratingType.value}_count`] > 0 || ratingType.value === 3
                    const matchesFilter = !filterText || KNOLIA.NormalizeString(u.name).includes(filterText)

                    u.hide = !(isRatingVisible && matchesFilter)
                    if (isRatingVisible && matchesFilter) {
                        anyUserMatched = true
                    }
                })

                if (anyUserMatched) {
                    out.push(team)
                }
            }

            return out
        }
    })

    const displayedRequests = computed(() => {
        
        if (validationList.value.length === 0 || !showUserRequests.value || !userSelected?.value) 
            return []
        
        const ratingTypeInt = parseInt(ratingType.value)
        const filterText = KNOLIA.NormalizeString(userFilter?.value)
        let list = (userSelected.value.requests || []).slice()

        list = list.filter(e => {
            if (e.rating_type !== ratingTypeInt && ratingTypeInt !== 3)
                return false

            if (!filterText) 
                return true

            const requestDesignation = KNOLIA.NormalizeString(e.designation || '')
            const requestValidator = KNOLIA.NormalizeString(e.validator_name || '')
            const requestDate = KNOLIA.NormalizeString(e.request_date || '')
            const validationDate = KNOLIA.NormalizeString(e.validation_date || '')

            return (
                requestDesignation.includes(filterText) || requestValidator.includes(filterText)   ||
                requestDate.includes(filterText)        || validationDate.includes(filterText)
            )
        })

        if (Array.isArray(list))
            sortUserRequestsArray(list, requestDateAsc?.value)

        return list
    })

    return {
        validateSkills,
        ratingType,
        userFilter,
        requestDateAsc,
        isMobile,
        isLowerThanMobile,
        validationList,
        showUserRequests,
        userSelected,
        filteredValidationList,
        displayedRequests,
        toggleAllTeams,
        validationWithCertification,
        validationToTransfer,
        showOnlySelf,
        GetValidationList,
        Validate,
        ChangeValidator,
        RequestValidationList,
        ToggleRequestDateSort,
        ShowUserRequests,
        parseDateSafe,
        sortUserRequestsArray,
        ToggleAllTeams,
        BackToTeams,
        TeamDetail,
        SetCommentsRead,
        SetDocumentsRead,
        ToggleValidationWithCertification,
        ToggleValidationWithoutCertification,
        ToggleValidationToTransfer,
        canChangeValidator,
    }
}